import React, { useEffect, useRef, useState } from "react";
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import TileWMS from 'ol/source/TileWMS';
import { get, fromLonLat, toLonLat } from 'ol/proj';
import MapPopUp from './MapPopUp';
import IACLegend from './IACLegend';
import AgricoleLegend from "./AgricoleLegend";
import LegendPanel from "./LegendPanel";

// Centre précis de la Montérégie
const CENTER_3857 = fromLonLat([-73.15, 45.35]);
const ZOOM = 9; // Zoom réduit pour la Montérégie

function MapView({ visibleLayers = [], activeLayer }) {
  const mapRef = useRef(null);
  const olMapRef = useRef(null);
  const [popup, setPopup] = useState(null);
  const [mouseCoords, setMouseCoords] = useState(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (olMapRef.current) {
      olMapRef.current.setTarget(null);
      olMapRef.current = null;
    }

    const layers = [
      new TileLayer({ source: new OSM() })
    ];

    if (visibleLayers && visibleLayers.length > 0) {
      visibleLayers.forEach(layerName => {
        layers.push(
          new TileLayer({
            source: new TileWMS({
              url: "https://mute-queen-37fegeoserver-proxy.morelstevensndong.workers.dev",
              params: {
                LAYERS: layerName,
                TILED: true,
                FORMAT: "image/png",
                TRANSPARENT: true,
                SRS: "EPSG:3857"
              },
              serverType: "geoserver"
            })
          })
        );
      });
    }

    const map = new Map({
      target: mapRef.current,
      layers,
      view: new View({
        center: CENTER_3857,
        zoom: ZOOM,
        projection: get("EPSG:3857")
      })
    });

    // Ajout du listener pour les coordonnées souris
    map.on('pointermove', (evt) => {
      if (evt.coordinate) {
        const [lon, lat] = toLonLat(evt.coordinate);
        setMouseCoords({
          lon: lon.toFixed(5),
          lat: lat.toFixed(5)
        });
      }
    });

    // Zoom automatique selon l'étendue WMS
    async function fitToLayerExtent() {
      if (!activeLayer) return;
      const capabilitiesUrl =
        "https://mute-queen-37fegeoserver-proxy.morelstevensndong.workers.dev?service=WMS&version=1.1.1&request=GetCapabilities";
      try {
        const response = await fetch(capabilitiesUrl);
        if (!response.ok) return;
        const text = await response.text();
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, "application/xml");
        // Cherche la couche active
        const layerNodes = xml.querySelectorAll("Layer");
        let bbox = null;
        layerNodes.forEach(layerNode => {
          const nameNode = layerNode.querySelector("Name");
          if (nameNode && nameNode.textContent === activeLayer) {
            const bboxNode = layerNode.querySelector("BoundingBox[CRS='EPSG:3857'], BoundingBox[SRS='EPSG:3857']");
            if (bboxNode) {
              bbox = [
                parseFloat(bboxNode.getAttribute("minx")),
                parseFloat(bboxNode.getAttribute("miny")),
                parseFloat(bboxNode.getAttribute("maxx")),
                parseFloat(bboxNode.getAttribute("maxy"))
              ];
            }
          }
        });
        if (bbox) {
          console.log('BBOX trouvée pour', activeLayer, bbox);
          map.getView().fit(bbox, { size: map.getSize(), maxZoom: 16 });
        } else {
          console.warn('Aucune BBOX trouvée pour', activeLayer);
          map.getView().setCenter(CENTER_3857);
          map.getView().setZoom(ZOOM);
        }
      } catch (e) {
        // ignore erreur
      }
    }
    fitToLayerExtent();

    // Gestion du clic pour GetFeatureInfo (OpenLayers natif)
    map.on('singleclick', async (evt) => {
      if (!activeLayer) return;
      const view = map.getView();
      const url = layers[layers.length - 1].getSource().getFeatureInfoUrl(
        evt.coordinate,
        view.getResolution(),
        view.getProjection(),
        {
          'INFO_FORMAT': 'application/json',
          'QUERY_LAYERS': activeLayer
        }
      );
      if (url) {
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error("Erreur serveur");
          const data = await response.json();
          if (data.features && data.features.length > 0) {
            const props = data.features[0].properties || data.features[0].attributes || data.features[0];
            setPopup({
              feature: props,
              position: { left: 100, top: 100 }
            });
          } else {
            setPopup({
              feature: { erreur: "Aucune donnée trouvée" },
              position: { left: 100, top: 100 }
            });
          }
        } catch (err) {
          setPopup({
            feature: { erreur: "Impossible d'accéder aux données (CORS ou serveur)" },
            position: { left: 100, top: 100 }
          });
        }
      }
    });

    olMapRef.current = map;
    return () => {
      map.setTarget(null);
    };
  }, [visibleLayers, activeLayer]);

  return (
    <div
      ref={mapRef}
      className="ol-map-container"
      style={{
        width: "100%",
        height: "100%",
        background: "#fff",
        position: "relative"
      }}
    >
      {popup && (
        <MapPopUp
          feature={popup.feature}
          position={popup.position}
          onClose={() => setPopup(null)}
        />
      )}
      {/* Affichage des coordonnées souris */}
      {mouseCoords && (
        <div
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            background: "rgba(32,40,32,0.85)",
            color: "#bfcf7f",
            padding: "0.35em 1em",
            borderRadius: "8px",
            fontSize: "1em",
            fontFamily: "monospace",
            zIndex: 10
          }}
        >
          Lon: {mouseCoords.lon} | Lat: {mouseCoords.lat}
        </div>
      )}
    </div>
  );
}

export default MapView;
